import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ref, push, get, set, update, remove } from '../lib/firebase-database'
import { ref as storageRef, uploadBytes, getDownloadURL } from '../lib/firebase-storage'
import { auth, db, storage } from '../firebase'
import { useAuth } from '../hooks/useAuth'
import { AdminLayout } from './AdminLayout'
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Textarea } from "./ui/textarea"
import { Label } from "./ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { ImagePlus, Trash2, Loader2, Edit, Plus, X, Calendar, User, ExternalLink } from 'lucide-react'

export default function BlogCreationPage() {
  const { user } = useAuth()
  const { blogId } = useParams()
  const [blog, setBlog] = useState({
    title: '',
    excerpt: '',
    content: '',
    images: [],
    readTime: 5,
    mainCity: '',
    link: '',
    createdAt: Date.now(),
    updatedAt: null
  })
  const [images, setImages] = useState([])
  const [imagesPreviews, setImagesPreviews] = useState([])
  const [cities, setCities] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [blogs, setBlogs] = useState([])
  const [showForm, setShowForm] = useState(false)
  const navigate = useNavigate()

  // Fetch unique main cities from villas
  useEffect(() => {
    const fetchCities = async () => {
      const villasRef = ref(db, 'villas')
      const snapshot = await get(villasRef)
      if (snapshot.exists()) {
        const uniqueCities = new Set()
        Object.values(snapshot.val()).forEach(villa => {
          if (villa.mainCity) {
            uniqueCities.add(villa.mainCity)
          }
        })
        setCities(Array.from(uniqueCities).sort())
      }
    }
    fetchCities()
  }, [])

  // Fetch blog data if editing
  useEffect(() => {
    const fetchBlog = async () => {
      if (blogId) {
        try {
          const blogRef = ref(db, `blogs/${blogId}`)
          const snapshot = await get(blogRef)
          if (snapshot.exists()) {
            const blogData = snapshot.val()
            setBlog(blogData)
            if (blogData.images) {
              setImagesPreviews(blogData.images)
            }
          }
        } catch (err) {
          setError('Failed to fetch blog')
        }
      }
    }
    fetchBlog()
  }, [blogId])

  // Fetch all blogs
  const fetchBlogs = async () => {
    try {
      const blogsRef = ref(db, 'blogs')
      const snapshot = await get(blogsRef)
      if (snapshot.exists()) {
        const blogsData = Object.entries(snapshot.val()).map(([id, blog]) => ({
          id,
          ...blog,
        }))
        setBlogs(blogsData.sort((a, b) => b.createdAt - a.createdAt))
      }
    } catch (error) {
      console.error('Error fetching blogs:', error)
    }
  }

  useEffect(() => {
    fetchBlogs()
  }, [])

  // Handle multiple image upload
  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files)
    const validFiles = files.filter(file => {
      if (file.size > 5242880) { // 5MB limit
        setError(`Image ${file.name} is larger than 5MB`)
        return false
      }
      return true
    })

    const newImagePreviews = validFiles.map(file => URL.createObjectURL(file))
    setImagesPreviews(prev => [...prev, ...newImagePreviews])
    setImages(prev => [...prev, ...validFiles])
  }

  // Remove image
  const handleRemoveImage = (index) => {
    setImagesPreviews(prev => prev.filter((_, i) => i !== index))
    setImages(prev => prev.filter((_, i) => i !== index))
  }

  const handleDelete = async (blogId, mainCity) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await remove(ref(db, `blogs/${blogId}`))
        await remove(ref(db, `cityBlogs/${mainCity.toLowerCase().replace(/\s+/g, '-')}/${blogId}`))
        setBlogs(blogs.filter(blog => blog.id !== blogId))
      } catch (error) {
        console.error('Error deleting blog:', error)
      }
    }
  }

  const handleEdit = (blog) => {
    setBlog(blog)
    setImagesPreviews(blog.images || [])
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      if (!blog.mainCity) {
        throw new Error('Please select a city')
      }

      if (images.length === 0 && !blogId && imagesPreviews.length === 0) {
        throw new Error('Please upload at least one image')
      }

      // Upload new images
      const uploadedImageUrls = []
      for (const image of images) {
        const imageRef = storageRef(storage, `blog-images/${Date.now()}_${image.name}`)
        await uploadBytes(imageRef, image)
        const imageUrl = await getDownloadURL(imageRef)
        uploadedImageUrls.push(imageUrl)
      }

      // Combine existing and new image URLs
      const allImageUrls = [...(blog.images || []), ...uploadedImageUrls]

      const blogData = {
        ...blog,
        images: allImageUrls,
        updatedAt: Date.now()
      }

      let finalBlogId = blogId

      if (blogId) {
        // Update existing blog
        await update(ref(db, `blogs/${blogId}`), blogData)
      } else {
        // Create new blog
        const newBlogRef = push(ref(db, 'blogs'))
        finalBlogId = newBlogRef.key
        await set(newBlogRef, { ...blogData, id: finalBlogId })
      }

      // Always update city-specific blog (this was missing in original code)
      const cityKey = blog.mainCity.toLowerCase().replace(/\s+/g, '-')
      await set(
        ref(db, `cityBlogs/${cityKey}/${finalBlogId}`),
        { ...blogData, id: finalBlogId }
      )

      alert(blogId ? 'Blog updated successfully!' : 'Blog created successfully!')
      
      // Refresh blogs list
      await fetchBlogs()
      setShowForm(false)
      resetForm()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setBlog({
      title: '',
      excerpt: '',
      content: '',
      images: [],
      readTime: 5,
      mainCity: '',
      link: '',
      createdAt: Date.now(),
      updatedAt: null
    })
    setImages([])
    setImagesPreviews([])
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <AdminLayout
      user={user}
      onLogout={() => auth.signOut().then(() => navigate('/login'))}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Location Blogs</h1>
            <p className="text-sm text-gray-500 mt-1">Manage blogs for different villa locations</p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center justify-center px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900 w-full sm:w-auto"
          >
            {showForm ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create New Blog
              </>
            )}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                {blog.id ? 'Edit Blog' : 'Create New Blog'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* City Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="city">Select City *</Label>
                    <select
                      id="city"
                      className="w-full mt-2 rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={blog.mainCity}
                      onChange={(e) => setBlog(prev => ({ ...prev, mainCity: e.target.value }))}
                      required
                    >
                      <option value="">Select a city</option>
                      {cities.map(city => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="readTime">Read Time (minutes) *</Label>
                    <Input
                      id="readTime"
                      type="number"
                      min="1"
                      max="60"
                      className="mt-2"
                      value={blog.readTime}
                      onChange={(e) => setBlog(prev => ({ ...prev, readTime: parseInt(e.target.value) }))}
                      required
                    />
                  </div>
                </div>

                {/* Title */}
                <div>
                  <Label htmlFor="title">Blog Title *</Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="Enter an engaging blog title"
                    className="mt-2"
                    value={blog.title}
                    onChange={(e) => setBlog(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <Label htmlFor="excerpt">Short Description *</Label>
                  <Textarea
                    id="excerpt"
                    placeholder="Write a compelling excerpt that summarizes your blog"
                    className="mt-2"
                    value={blog.excerpt}
                    onChange={(e) => setBlog(prev => ({ ...prev, excerpt: e.target.value }))}
                    required
                    maxLength={200}
                    rows={3}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {200 - blog.excerpt.length} characters remaining
                  </p>
                </div>

                {/* Content */}
                <div>
                  <Label htmlFor="content">Blog Content *</Label>
                  <Textarea
                    id="content"
                    placeholder="Write your detailed blog content here..."
                    className="mt-2 min-h-[200px]"
                    value={blog.content}
                    onChange={(e) => setBlog(prev => ({ ...prev, content: e.target.value }))}
                    required
                  />
                </div>

                {/* External Link */}
                <div>
                  <Label htmlFor="link">External Link (Optional)</Label>
                  <Input
                    id="link"
                    type="url"
                    placeholder="https://example.com/full-blog-post"
                    className="mt-2"
                    value={blog.link}
                    onChange={(e) => setBlog(prev => ({ ...prev, link: e.target.value }))}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Link to the full blog post if hosted elsewhere
                  </p>
                </div>

                {/* Images */}
                <div>
                  <Label>Blog Images *</Label>
                  <div className="mt-2 space-y-4">
                    {/* Image Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {imagesPreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview || "/placeholder.svg"}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      
                      {/* Upload Button */}
                      <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <ImagePlus className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-500 text-center px-2">
                          Add Images
                        </span>
                        <span className="text-xs text-gray-400">Max 5MB each</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          multiple
                          onChange={handleImagesChange}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full sm:w-auto px-8"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {blogId ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      blogId ? 'Update Blog' : 'Create Blog'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Blogs Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              All Blogs ({blogs.length})
            </h2>
          </div>

          {blogs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Edit className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No blogs yet</h3>
                <p className="text-gray-500 mb-4">Create your first location blog to get started</p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Blog
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {blogs.map(blog => (
                <Card key={blog.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-video relative">
                    <img
                      src={blog.images?.[0] || "/placeholder.svg?height=200&width=300"}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="bg-white/90">
                        {blog.mainCity}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <h3 className="font-bold text-lg line-clamp-2 leading-tight">
                        {blog.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {blog.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(blog.createdAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {blog.readTime} min read
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(blog)}
                          className="flex-1"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        
                        {blog.link && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(blog.link, '_blank')}
                            className="flex-1"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(blog.id, blog.mainCity)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
